import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Invoice, Client, InvoiceEditValues, Company, InvoiceItem } from "@/types/invoice";
import { useToast } from "@/hooks/use-toast";
import { useEffect, useState } from "react";
import { fromUTC, parsePlainDateString, toUTC } from "@/utils/dateTransforms";
import { getAssignmentWorkingHours } from "@/utils/assignmentUtils";
import { Assignment, Employee, Project } from "@/components/assignments/types";
import { getPersonShortName } from "@/utils/nameFormatters";
import { parseAssignmentData, RawAssignmentData } from "../assignments/parseAssignments";
import { useUser } from "@/contexts/UserContext";

export const useInvoiceDetail = (invoiceId: string | undefined) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [client, setClient] = useState<Client | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [invoiceItems, setInvoiceItems] = useState<InvoiceItem[]>([]);
  const { companyId } = useUser(); // Get user data from UserContext
  
  const { data: invoice, isLoading, error } = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: async () => {
      if (!invoiceId) throw new Error('No invoice ID provided');
      
      const { data, error } = await supabase
        .from('invoices')
        .select(`
          id,
          client_id,
          invoice_number,
          amount,
          currency,
          status,
          billing_period_start,
          billing_period_end,
          milestone_id,
          paid_at,
          sent_at,
          items
        `)
        .eq('id', parseInt(invoiceId))
        .single();
        
      if (error) {
        console.error("Error fetching invoice:", error);
        throw error;
      }
      
      return {
        ...data,
        items: data.items as unknown as InvoiceItem[]
      } as Invoice;
    },
    enabled: !!invoiceId
  });
  
  useEffect(() => {
    if (invoice?.items) {
      if (Array.isArray(invoice.items) && invoice.items.length > 0) {
        const validItems = (invoice.items as InvoiceItem[]).map(item => ({
          id: item.id || Math.random(),
          description: item.description || "",
          quantity: typeof item.quantity === 'number' ? item.quantity : 1,
          rate: typeof item.rate === 'number' ? item.rate : 0,
          amount: typeof item.amount === 'number' ? item.amount : 0,
          source: item.source || 'manual',
          modified: item.modified || false
        }));
        setInvoiceItems(validItems);
      } else {
        setInvoiceItems([{
          id: 1,
          description: "",
          quantity: 1,
          rate: 0,
          amount: 0,
          source: 'manual',
          modified: false
        }]);
      }
    }
  }, [invoice?.items]);
  
  useEffect(() => {
    const fetchClientAndCompany = async () => {
      if (!invoice?.client_id || !companyId) return;
      
      try {
        const { data, error } = await supabase
          .from('clients')
          .select(`
            id, 
            name,
            short_name,
            legal_name,
            billing_details
          `)
          .eq('id', invoice.client_id)
          .single();
          
        if (error) throw error;
        setClient(data as Client);
        
        // Use companyId from UserContext instead of fetching it
        const { data: companyData, error: companyError } = await supabase
          .from('companies')
          .select(`
            id,
            name,
            legal_name,
            logo_url,
            address,
            bank_details
          `)
          .eq('id', companyId)
          .single();
          
        if (companyError) throw companyError;
        setCompany(companyData as Company);
      } catch (err) {
        console.error("Error fetching client and company:", err);
      }
    };
    
    fetchClientAndCompany();
  }, [invoice?.client_id, companyId]);
  
  const updateInvoiceMutation = useMutation({
    mutationFn: async (values: InvoiceEditValues) => {
      if (!invoiceId) throw new Error('No invoice ID provided');
      
      const totalAmount = invoiceItems.reduce((sum, item) => sum + (item.amount || 0), 0);
      
      const { error } = await supabase
        .from('invoices')
        .update({
          amount: totalAmount,
          status: values.status,
          currency: values.currency,
          billing_period_start: values.billing_period_start,
          billing_period_end: values.billing_period_end,
          sent_at: values.sent_at,
          items: invoiceItems as any,
          ...(values.status === 'paid' && !invoice?.paid_at ? { paid_at: new Date().toISOString() } : {})
        })
        .eq('id', parseInt(invoiceId));
        
      if (error) throw error;
      
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      toast({
        title: "Invoice updated",
        description: "Changes have been saved successfully",
      });
    },
    onError: (error) => {
      console.error("Error updating invoice:", error);
      toast({
        title: "Error updating invoice",
        description: "Please try again",
        variant: "destructive",
      });
    }
  });

  const updateInvoiceItem = (id: number, field: keyof InvoiceItem, value: string) => {
    setInvoiceItems(prevItems => {
      const newItems = [...prevItems];
      const itemIndex = newItems.findIndex(item => item.id === id);
      
      if (itemIndex !== -1) {
        const newItem = { ...newItems[itemIndex] };
        
        if (field === 'description') {
          newItem.description = value;
        } else if (field === 'quantity') {
          const quantity = parseFloat(value);
          newItem.quantity = isNaN(quantity) ? 1 : quantity;
          newItem.amount = newItem.quantity * (newItem.rate || 0);
        } else if (field === 'rate') {
          const rate = parseFloat(value);
          newItem.rate = isNaN(rate) ? 0 : rate;
          newItem.amount = (newItem.quantity || 1) * newItem.rate;
        }
        
        if (newItem.source === 'auto') {
          newItem.modified = true;
        }
        
        newItems[itemIndex] = newItem;
      }
      
      const totalAmount = newItems.reduce((sum, item) => sum + (item.amount || 0), 0);
      if (invoice) {
        updateInvoiceMutation.mutate({
          ...invoice,
          amount: totalAmount
        });
      }
      
      return newItems;
    });
  };

  const deleteInvoiceItem = (id: number) => {
    setInvoiceItems(prevItems => {
      if (prevItems.length <= 1) {
        return prevItems;
      }
      
      const newItems = prevItems.filter(item => item.id !== id);
      
      const totalAmount = newItems.reduce((sum, item) => sum + (item.amount || 0), 0);
      if (invoice) {
        updateInvoiceMutation.mutate({
          ...invoice,
          amount: totalAmount
        });
      }
      
      return newItems;
    });
  };

  const addInvoiceItem = () => {
    setInvoiceItems(prevItems => {
      const newId = prevItems.length > 0 
        ? Math.max(...prevItems.map(item => item.id)) + 1 
        : 1;
        
      const newItem: InvoiceItem = {
        id: newId,
        description: "",
        quantity: 1,
        rate: 0,
        amount: 0
      };
      
      return [...prevItems, newItem];
    });
  };

  const syncAssignments = async () => {
    if (!invoice || !client) {
      toast({
        title: "Cannot sync assignments",
        description: "Invoice or client data is missing",
        variant: "destructive",
      });
      return;
    }
    
    try {
      console.log("Fetching assignments with parameters:");
      console.log("Billing period start:", invoice.billing_period_start);
      console.log("Billing period end:", invoice.billing_period_end);
      console.log("Client ID:", client.id);
      
      const startDate = `${invoice.billing_period_start}T00:00:00Z`;
      const endDate = `${invoice.billing_period_end}T23:59:59Z`;
      
      const { data: rawAssignments, error: assignmentsError } = await supabase
        .from('employee_project_assignments')
        .select(`
          id,
          hourly_rate,
          start_date,
          end_date,
          employee_id,
          daily_hours,
          isBillable,
          hour_overrides,
          employee:employees(id, name, role),
          project:projects(id, name, code)
        `)
        .or(`start_date.lte.${endDate},end_date.gte.${startDate}`)
        .eq('project.client_id', client.id)
        .returns<(RawAssignmentData & {
          employee: { id: number, name: string, role: string },
          project: { id: number, name: string, code: string }
        })[]>();
        
      if (assignmentsError) throw assignmentsError;
      
      const assignments = parseAssignmentData(rawAssignments);
      
      if (!assignments || assignments.length === 0) {
        toast({
          title: "No assignments found",
          description: "No employee assignments found for this client during the billing period",
        });
        return;
      }
      
      // Filter out non-billable assignments
      const billableAssignments = assignments.filter(assignment => 
        assignment.isBillable !== false && 
        assignment && assignment.project !== null && 
        typeof assignment.id === 'number'
      );
      
      console.log("Billable assignments after filtering:", billableAssignments);
      
      if (billableAssignments.length === 0) {
        toast({
          title: "No billable assignments",
          description: "No billable assignments found for this client during the billing period",
        });
        return;
      }
      
      setInvoiceItems(prevItems => {
        const manualItems = prevItems.filter(item => item.source !== 'auto');
        
        const totalAmount = manualItems.reduce((sum, item) => sum + (item.amount || 0), 0);
        if (invoice) {
          updateInvoiceMutation.mutate({
            ...invoice,
            amount: totalAmount
          });
        }
        
        return manualItems;
      });
      
      const groupedAssignments = billableAssignments.reduce((groups, assignment) => {
        const employee = assignment.employee as unknown as { id: number, name: string, role: string };
        const project = assignment.project as unknown as { id: number, name: string, code: string };
        const rate = assignment.hourly_rate;
        const key = `${employee.id}-${project.id}-${rate}`;
        
        if (!groups[key]) {
          groups[key] = {
            employee,
            project,
            rate,
            assignments: []
          };
        }
        
        groups[key].assignments.push(assignment);
        
        return groups;
      }, {} as Record<string, { 
        employee: { id: number, name: string, role: string },
        project: { id: number, name: string, code: string },
        rate: number, 
        assignments: any[]
      }>);
      
      const newItems: InvoiceItem[] = [];
      let nextItemId = 1;
      
      setInvoiceItems(prevItems => {
        if (prevItems.length > 0) {
          nextItemId = Math.max(...prevItems.map(item => item.id)) + 1;
        }
        return prevItems;
      });
      
      // Get invoice billing period as date objects
      const startDateObj = parsePlainDateString(invoice.billing_period_start);
      const endDateObj = parsePlainDateString(invoice.billing_period_end);
      
      for (const groupKey in groupedAssignments) {
        const group = groupedAssignments[groupKey];
        let totalHours = 0;
        
        console.log(`Processing group: Employee ${group.employee.name}, Project ${group.project.name}`);
        
        for (const assignment of group.assignments) {
          const assignmentForHours: Assignment = {
            id: assignment.id,
            employee_id: assignment.employee_id,
            hourly_rate: assignment.hourly_rate,
            daily_hours: assignment.daily_hours !== null ? assignment.daily_hours : 8,
            start_date: assignment.start_date,
            end_date: assignment.end_date,
            project: assignment.project as Assignment['project'],
            isBillable: assignment.isBillable !== false,
            hour_overrides: Array.isArray(assignment.hour_overrides) 
              ? assignment.hour_overrides.filter(override => override.isBillable !== false)
              : []
          };
          
          // Log input parameters for debugging
          console.log(`Assignment details for ${assignment.id}:`, {
            daily_hours: assignmentForHours.daily_hours,
            start_date: assignmentForHours.start_date,
            end_date: assignmentForHours.end_date,
            billing_period_start: startDateObj,
            billing_period_end: endDateObj,
            overrides: assignmentForHours.hour_overrides
          });
          
          const hours = getAssignmentWorkingHours(assignmentForHours, startDateObj, endDateObj, true);
          console.log(`Hours calculated for assignment ${assignment.id}: ${hours}`);
          
          totalHours += hours;
        }
        
        console.log(`Total hours for group: ${totalHours}`);
        
        if (totalHours > 0) {
          const shortName = getPersonShortName(group.employee.name);
          const description = `${group.project.code} - ${shortName} (${group.employee.role})`;
          const amount = totalHours * group.rate;
          
          newItems.push({
            id: nextItemId++,
            description,
            quantity: totalHours,
            rate: group.rate,
            amount,
            source: 'auto',
            modified: false
          });
          
          console.log(`Added invoice item: ${description}, Hours: ${totalHours}, Rate: ${group.rate}`);
        }
      }
      
      if (newItems.length > 0) {
        setInvoiceItems(prevItems => {
          const updatedItems = [...prevItems, ...newItems];
          
          const totalAmount = updatedItems.reduce((sum, item) => sum + (item.amount || 0), 0);
          if (invoice) {
            updateInvoiceMutation.mutate({
              ...invoice,
              amount: totalAmount
            });
          }
          
          return updatedItems;
        });
        
        toast({
          title: "Assignments synced",
          description: `Added ${newItems.length} assignments to the invoice`,
        });
      } else {
        toast({
          title: "No billable hours",
          description: "Found assignments but no billable hours within the billing period",
        });
      }
    } catch (error) {
      console.error("Error syncing assignments:", error);
      toast({
        title: "Error syncing assignments",
        description: "Please try again",
        variant: "destructive",
      });
    }
  };
  
  return {
    invoice,
    client,
    company,
    invoiceItems,
    isLoading,
    error,
    updateInvoice: updateInvoiceMutation.mutate,
    isUpdating: updateInvoiceMutation.isPending,
    updateInvoiceItem,
    deleteInvoiceItem,
    addInvoiceItem,
    syncAssignments
  };
};
